import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {makeAutoObservable, observable, runInAction, flow, flowResult} from "mobx";
import {Variants, Variant} from "framer-motion";
import {wait} from "data/utils";
import {IAnimation} from "data/types/animation";
import {Bindings} from "data/constants/bindings";
import {times} from "lodash";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {PlayerCategory} from "data/enums";

interface IParams {
	size: number;
	radius: number;
	prize: number | null;
	prizeIndex: number | null;
	isLimitReached: boolean;
	cellCategories: (PlayerCategory | undefined)[];
}

export interface ISpinnerController extends ViewController<IParams> {
	readonly i18n: ILocalizationStore;

	get spinnerAnimation(): IAnimation;
	get lightsAnimation(): IAnimation;
	get segmentsAnimations(): IAnimation[];
	get resultAnimation(): IAnimation;
	get pointerAnimation(): IAnimation;
	get missedPlayersShadowAnimation(): IAnimation;
	get missedPlayersItemAnimation(): IAnimation;
	get borderGradientAnimation(): IAnimation;
	get spinCountAnimation(): IAnimation;
	get spinCountCenterAnimation(): IAnimation;
	get timeCountAnimation(): IAnimation;

	updateTime: (time: number) => void;
	get cellAnimationStates(): boolean[];
}

// const gradient = "linear-gradient(180deg, #21CFDC 0%, #99FE88 100%)";
@injectable()
export class SpinnerController implements ISpinnerController {
	private _animationFlow: ReturnType<typeof flowResult<Generator>> | null = null;
	private _size: number = 0;
	private _isLimitReached: boolean = false;
	private _radius: number = 0;
	private _prize: number | null = null;
	private _cellCategories: IParams["cellCategories"] = [];
	@observable private _cellAnimations: boolean[] = [];
	@observable private _cellAnimationFlow?: ReturnType<typeof flowResult<Generator>>;

	@observable _spinnerAnimation = "initial";
	@observable _lightsAnimation = "initial";
	@observable _segmentsAnimation = "initial";
	@observable _resultAnimation = "initial";
	@observable _pointerAnimation = "initial";
	@observable _missedPlayersShadowAnimation = "initial";
	@observable _missedPlayersItemAnimation = "initial";
	@observable _borderGradientAnimation = "initial";
	@observable _spinCountAnimation = "initial";
	@observable _spinCountCenterAnimation = "initial";
	@observable _timeCountAnimation = "initial";

	constructor(
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	private get isLimit() {
		return this._isLimitReached;
	}

	get spinnerAnimation() {
		const variants: Variants = {
			initial: {
				rotate: 0,
				scale: 0,
				transition: {
					duration: 0,
				},
			} as Variant,
			show: {
				rotate: 0,
				scale: 1,
				transition: {
					rotate: {
						duration: 0,
					},
				},
			} as Variant,
			start: {
				scale: 1,
				rotate: this.prizeAnimationOffset,
				transition: {
					ease: "circInOut",
					duration: 4,
				},
			} as Variant,
		};

		return {
			variants: variants,
			initial: "initial",
			type: this._spinnerAnimation,
		};
	}

	get lightsAnimation(): IAnimation {
		const variants: Variants = {
			initial: {
				"--blur-dot-size": "0px",
				rotate: 0,
			} as Variant,
			show: {
				"--blur-dot-size": "4px",
				rotate: 0,
				transition: {
					"--blur-dot-size": {
						repeat: Infinity,
						repeatType: "reverse",
						type: "spring",
						damping: 10,
						mass: 0.75,
						stiffness: 100,
					},
					rotate: {
						duration: 0,
					},
				},
			} as Variant,

			start: {
				"--blur-dot-size": "4px",
				rotate: this.prizeAnimationOffset,
				transition: {
					ease: "circInOut",
					duration: 4,
					"--blur-dot-size": {
						repeat: Infinity,
						repeatType: "reverse",
						type: "spring",
						damping: 10,
						mass: 0.75,
						stiffness: 100,
					},
				},
			} as Variant,
		};

		return {
			variants: variants,
			initial: "initial",
			type: this._spinnerAnimation,
		};
	}

	get segmentsAnimations(): IAnimation[] {
		return times(this._size, (n) => n).map((i) => {
			const angleCoef = (i + 1) / this._size;
			const offset = this.offset;
			return {
				variants: {
					initial: {
						width: this.hordeWith,
						height: this._radius,
						rotate: 360 * angleCoef + offset,
						clipPath: "polygon(50% 100%, 100% 0, 100% 0, 0 0, 0 0)",
					},
					blur: {
						filter: "blur(2px)",
					},
					endBlur: {
						filter: "blur(0px)",
					},
				},
				type: this._segmentsAnimation,
				initial: "initial",
			};
		});
	}

	get resultAnimation(): IAnimation {
		return {
			variants: {
				initial: {
					scale: 0,
					pointerEvents: "none",
				},
				start: {
					scale: 1,
					display: "block",
					pointerEvents: "all",
				},
			},
			type: this._resultAnimation,
			initial: "initial",
		};
	}

	get pointerAnimation(): IAnimation {
		return {
			variants: {
				initial: {
					scale: 0,
					color: "#D9DEE4",
				},
				start: {
					scale: 1,
					display: "block",
					pointerEvents: "all",
					color: "var(--wheel-view-pointer_background-color)",
				},
				warning: {
					scale: 1,
					display: "block",
					pointerEvents: "all",
					color: "#FF583E",
				},
				end: {
					scale: 1,
					display: "block",
					pointerEvents: "all",
					color: "#D9DEE4",
				},
			},
			type: this._pointerAnimation,
			initial: "initial",
		};
	}

	get missedPlayersShadowAnimation(): IAnimation {
		return {
			variants: {
				initial: {
					width: "100vw",
					left: "50%",
					x: "-50%",
					zIndex: 0,
					pointerEvents: "none",
					transition: {
						type: "false",
						delayChildren: 0.5,
					},
				},
				hide: {
					opacity: 0,
					transition: {
						type: "false",
						delayChildren: 0.5,
					},
				},
				show: {
					opacity: 1,
					display: "block",
					pointerEvents: "all",
					left: "50%",
					zIndex: 3,
					x: "-50%",
					transition: {
						type: "false",
						delayChildren: 0.5,
					},
				},
				start: {
					zIndex: 3,
					pointerEvents: "all",
					transition: {
						type: "false",
						delayChildren: 0.5,
					},
				},
			},
			type: this._missedPlayersShadowAnimation,
			initial: "initial",
		};
	}

	get missedPlayersItemAnimation(): IAnimation {
		return {
			variants: {
				initial: {
					scale: 0,
				},
				start: {
					scale: 1,
					transition: {
						type: "spring",
					},
				},
			},
			type: this._missedPlayersItemAnimation,
			initial: "initial",
		};
	}

	get borderGradientAnimation() {
		const variants: Variants = {
			initial: {
				background: "var(--wheel-view-border_background-color)",
				"--border-gradient-deg-start": "0deg",
				"--border-gradient-deg-warning": "0deg",
				"--border-gradient-deg-error": "0deg",
			} as Variant,
			blur: {
				filter: "blur(2px)",
			},
			endBlur: {
				filter: "blur(0px)",
			},
			start: {
				"--border-gradient-deg-start": "1turn",
				background:
					"conic-gradient(from 0deg at 50% 50%, #D9DEE4 0deg, #D9DEE4 var(--border-gradient-deg-start), #777A7D var(--border-gradient-deg-start), #777A7D 360deg)",
				transition: {
					"--border-gradient-deg-start": {
						duration: 5,
					},
				},
			} as Variant,

			warning: {
				"--border-gradient-deg-warning": "1turn",
				background:
					"conic-gradient(from 0deg at 50% 50%, #CC4632 0deg, #CC4632 var(--border-gradient-deg-warning), #FF583E var(--border-gradient-deg-warning), #FF583E 360deg)",
				transition: {
					"--border-gradient-deg-warning": {
						duration: 2.5,
					},
				},
			} as Variant,
			error: {
				"--border-gradient-deg-error": "1turn",
				background:
					"conic-gradient(from 0deg at 50% 50%, #661104 0deg, #661104 var(--border-gradient-deg-error), #CC2308 var(--border-gradient-deg-error), #CC2308 360deg)",
				transition: {
					"--border-gradient-deg-error": {
						duration: 4,
					},
				},
			} as Variant,
		};

		return {
			variants: variants,
			initial: "initial",
			type: this._borderGradientAnimation,
		};
	}

	get spinCountAnimation(): IAnimation {
		return {
			variants: {
				initial: {
					scale: 0,
					"--blur-spin-center": "0px",
				} as Variant,
				start: {
					scale: 1,
					"--blur-spin-center": "0px",
				} as Variant,
				jump: {
					scale: 1.2,
					"--blur-spin-center": "8px",
					transition: {
						type: "spring",
						duration: 0.8,
						repeatType: "reverse",
						bounce: 0.5,
						repeat: 1,
						delay: 1,
						repeatDelay: 0.3,
					},
				} as Variant,
				end: {
					scale: 0,
				},
			},
			initial: "initial",
			type: this._spinCountAnimation,
		};
	}

	get spinCountCenterAnimation(): IAnimation {
		return {
			variants: {
				initial: {
					scale: 0,
					top: "50%",
					left: "50%",
					x: "-50%",
					y: "-50%",
					"--blur-spin-center": "0px",
				} as Variant,
				start: {
					scale: 1,
				},
				jump: {
					scale: 1.2,
					"--blur-spin-center": "8px",
					transition: {
						type: "spring",
						duration: 0.8,
						repeatType: "reverse",
						bounce: 0.5,
						repeat: 1,
						delay: 1,
						repeatDelay: 0.3,
					},
				} as Variant,
				end: {
					scale: 0,
				},
			},
			initial: "initial",
			type: this._spinCountCenterAnimation,
		};
	}

	get timeCountAnimation(): IAnimation {
		const variants: Variants = {
			initial: {
				scale: 0,
				"--rotate-circle-count-background": "0deg",
				"--circle-count-background":
					"#99fe88 conic-gradient(from var(--rotate-circle-count-background, 0deg) at 50% 50%, #99fe88 0deg, #52e548 360deg)",
			} as Variant,
			start: {
				scale: 1,
				"--rotate-circle-count-background": "360deg",
				transition: {
					"--rotate-circle-count-background": {
						repeat: 10,
						repeatType: "loop",
						duration: 1,
					},
				},
			} as Variant,
			warning: {
				scale: 1,
				"--rotate-circle-count-background": "360deg",
				"--circle-count-background":
					"#FF583E conic-gradient(from var(--rotate-circle-count-background, 0deg) at 50% 50%, #FF583E 0deg, #CC4632 360deg)",
			} as Variant,
		};

		return {
			variants: variants,
			initial: "initial",
			type: this._timeCountAnimation,
		};
	}

	onChange(params: IParams) {
		this._radius = params.radius;
		this._size = params.size;

		this._isLimitReached = params.isLimitReached;
		this._cellCategories = params.cellCategories;

		if (params.prizeIndex === null) {
			void this.setDefaultAnimations();
			return;
		}

		if (params.prizeIndex !== -1) {
			this._prize = params.prizeIndex;
			this.cancelCellAnimationFLow();
			this._animationFlow = flowResult(this.runSpinnerAnimation());
			this._animationFlow.catch((reason) => {
				const error = reason as Error;
				if (error.message === "FLOW_CANCELLED") {
					//ignore the error as it's expected
				}
			});
		}
	}

	async init(params: IParams) {
		this._radius = params.radius;
		this._size = params.size;
		this._isLimitReached = params.isLimitReached;
		this._cellCategories = params.cellCategories;
		await wait(500);

		runInAction(() => {
			this._spinnerAnimation = "show";
			this._lightsAnimation = "show";
			this._timeCountAnimation = "initial";
			this._spinCountCenterAnimation = "start";
		});

		void this.startCellAnimationFlow();
	}

	get cellAnimationStates() {
		return this._cellAnimations;
	}

	private async startCellAnimationFlow() {
		await wait(5000);
		this._cellAnimationFlow?.cancel();
		this._cellAnimationFlow = flowResult(this.runCycleCellAnimation());
		this._cellAnimationFlow.catch((reason) => {
			const error = reason as Error;
			if (error.message === "FLOW_CANCELLED") {
				//ignore the error as it's expected
			}
		});
	}
	@flow
	*runCycleCellAnimation() {
		const currentAnimationIndex = this._cellAnimations.findIndex((state) => state);
		const cellAnimations: boolean[] = [];

		this._cellCategories.forEach((category, categoryIndex) => {
			const hasAnimation = cellAnimations.some(Boolean);
			if (hasAnimation) {
				cellAnimations[categoryIndex] = false;
				return;
			}

			if (category && category !== PlayerCategory.NORMAL) {
				cellAnimations[categoryIndex] = currentAnimationIndex < categoryIndex;
				return;
			}
			cellAnimations[categoryIndex] = false;
		});

		this._cellAnimations = cellAnimations;

		yield wait(3500);

		this._cellAnimationFlow = flowResult(this.runCycleCellAnimation());
		this._cellAnimationFlow.catch((reason) => {
			const error = reason as Error;
			if (error.message === "FLOW_CANCELLED") {
				//ignore the error as it's expected
			}
		});
	}

	private cancelCellAnimationFLow() {
		this._cellAnimations = [];
		this._cellAnimationFlow?.cancel();
	}

	@flow
	private *runSpinnerAnimation() {
		yield wait(500);

		this._spinnerAnimation = "start";
		this._lightsAnimation = "start";
		this._pointerAnimation = "start";
		this._segmentsAnimation = "blur";
		this._borderGradientAnimation = "blur";

		yield wait(4000);

		this._segmentsAnimation = "endBlur";
		this._borderGradientAnimation = "endBlur";

		yield wait(500);
		this._spinCountCenterAnimation = "end";

		this._resultAnimation = "start";

		yield wait(600);

		this._missedPlayersShadowAnimation = "start";

		yield wait(400);

		this._missedPlayersItemAnimation = "start";

		yield wait(1000);

		this._spinCountAnimation = "start";

		if (!this.isLimit) {
			this._timeCountAnimation = "start";
		}

		yield wait(1000);
		this._spinCountAnimation = "jump";
	}

	updateTime = (time: number) => {
		if (!time) {
			this._missedPlayersItemAnimation = "initial";
			this._timeCountAnimation = "initial";
			return;
		}

		if (time <= 3) {
			this._borderGradientAnimation = "error";
			return;
		}

		if (time <= 5) {
			this._timeCountAnimation = "warning";
			this._borderGradientAnimation = "warning";
			this._pointerAnimation = "warning";
			return;
		}

		if (time <= 10) {
			this._pointerAnimation = "end";
			this._borderGradientAnimation = "start";
		}
	};

	private get hordeWith() {
		return 2 * this._radius * Math.tan(Math.PI / this._size) + 1;
	}

	private get turnsPerItem() {
		return 360 / this._size;
	}

	private get offset() {
		// shift wheel to be between two segments
		return (-1 * this.turnsPerItem) / 2;
	}

	private get prizeAnimationOffset() {
		return -this.turnsPerItem * (this._prize || 0) + 360 * 7 + this.offset;
	}

	private async setDefaultAnimations() {
		this._animationFlow?.cancel();
		void this.startCellAnimationFlow();
		this._spinnerAnimation = "show";
		this._segmentsAnimation = "initial";
		this._resultAnimation = "initial";
		this._pointerAnimation = "initial";
		this._missedPlayersShadowAnimation = "initial";
		this._borderGradientAnimation = "initial";
		this._missedPlayersShadowAnimation = "initial";
		this._missedPlayersItemAnimation = "initial";
		this._spinCountAnimation = "initial";
		this._spinCountCenterAnimation = "start";
		this._timeCountAnimation = "initial";
		await wait(1000);
		runInAction(() => {
			this._spinCountCenterAnimation = "jump";
		});
	}
}
